import { Confirm } from "/WebInterface/new-ui/modules/alert.js";
import { growl } from "/WebInterface/new-ui/modules/toast.js";
import { t } from "/WebInterface/new-ui/modules/localizations/localizations.js";
import downloadingQueue from "/WebInterface/new-ui/modules/advanced-download/downloading-queue.js";
import store from "/WebInterface/new-ui/store/index.js";
import C from "/WebInterface/new-ui/assets/js/utils/log.js";
import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";

// Cancel downloading file
export const cancelDownload = async ({
  streamingFile,
  writableStream,
  beforeCancelingFile = () => {},
  afterCancelingFile = () => {},
}) => {
  const file = streamingFile;
  const { id: fileId } = file;

  beforeCancelingFile();

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "canceled",
      downloadingError: "canceled",
    },
  });

  try {
    await writableStream.abort();
    await writableStream.close();
    try {
      await file.fileHandle.remove();
    } catch (ex) {}
  } catch (error) {
    C.log(error);
  }

  afterCancelingFile();
};

// Writes chunks in the disk using the file handle (FileSystemAPI :- FileHandle)
export const writeDownloadedChunksToDisk = async (parent) => {
  if (parent.fileClosed) {
    C.warn("Download: File is closed, continuing writing chunks to disk");
  }
  C.store("Download: write chunks to disk called");
  const this_parent = parent;

  const { size: fileSize, id: fileId } = this_parent.streamingFile;
  const writableStream = this_parent.writableStream;

  // Writes file in the disk while chain of the chunks break
  while (true) {
    const index = this_parent.chunkWrittenToTheDisk;
    const chunk = this_parent.chunksRemainsToWrite[index];

    if (!chunk) {
      C.store(
        `Download: expected index not available in chunksRemainsToWrite so breaking the loop ${index}`
      );
      const downloadDone =
        Object.keys(this_parent.chunksBeingWritten).filter(
          (key) =>
            !Object.prototype.hasOwnProperty.call(
              this_parent.chunksBeingWritten,
              key
            )
        ).length === 0 &&
        Object.keys(this_parent.chunksRemainsToWrite).filter(
          (key) =>
            !Object.prototype.hasOwnProperty.call(
              this_parent.chunksRemainsToWrite,
              key
            )
        ).length === 0;
      // If file completed to write close it
      if (this_parent.fileDownloaded && downloadDone) {
        C.warn(
          `Download: Interesting case when EOF received but index mismatches, Nothing left to write, nothing being written. Last received: ${this_parent.lastReceivedChunkIndex}, Last written: ${this_parent.lastWrittenChunkIndex}`
        );

        if (!this_parent.closeFileInProgress) {
          this_parent.closeFileDownloading();
          C.warn(`Download: File download done, breaking the loop`);
        }
      }
      break;
    } else {
      const { data, size } = chunk;
      try {
        this_parent.lastWrittenChunkIndex = index;
        C.log(`Download: Writing chunk ${index}`);
        this_parent.chunkWrittenToTheDisk++;
        this_parent.chunksBeingWritten[index] = index;
        await writableStream.write(data);
        this_parent.chunksWritten[index] = index;
        delete this_parent.chunksBeingWritten[index];
        C.log(`Download: Wrote chunk ${index}`);
        this_parent.lastIndexOfChunkWrittenToDisk = index;
        this_parent.bytesWritten += size;

        C.log(
          `Download: Written ${
            this_parent.bytesWritten
          }/${fileSize} (${this_parent.getPercentage(
            fileSize,
            this_parent.bytesWritten
          )}%)`
        );

        if (!this_parent.fileClosed && !this_parent.fileDownloaded) {
          store.dispatch("updateFileStatusDownloadDisk", {
            fileId,
            bytesWritten: this_parent.bytesWritten,
            fileSize,
          });
        }

        delete this_parent.chunksRemainsToWrite[index];

        const downloadDone =
          Object.keys(this_parent.chunksBeingWritten).filter(
            (key) =>
              !Object.prototype.hasOwnProperty.call(
                this_parent.chunksBeingWritten,
                key
              )
          ).length === 0 &&
          Object.keys(this_parent.chunksRemainsToWrite).filter(
            (key) =>
              !Object.prototype.hasOwnProperty.call(
                this_parent.chunksRemainsToWrite,
                key
              )
          ).length === 0;
        this_parent.checkIfFileDownloaded();
        // If file completed to write close it
        if (this_parent.fileDownloaded) {
          if (downloadDone) {
            if (!this_parent.closeFileInProgress) {
              this_parent.closeFileDownloading();
              C.warn(`Download: File download done, breaking the loop`);
            }
            break;
          } else {
            C.warn(
              `Download: File downloaded, but chunks are still remaining to write, continuing the loop`
            );
          }
        }
        // C.log(
        //   `Download: Chunk written to disk ${index}, next index is ${this_parent.chunkWrittenToTheDisk}`
        // );
      } catch (error) {
        C.error("Download: Error in write chunk to disk: " + error);
        break;
      }
    }
  }

  let pendingChunks = 0;
  let sumOfSize = 0;
  let pendingChunkNo = [];
  const bytesToMegabytes = (bytes) => {
    return bytes / (1024 * 1024);
  };
  const beingWritten = [];
  for (const key in this_parent.chunksBeingWritten) {
    if (this_parent.chunksBeingWritten.hasOwnProperty(key)) {
      beingWritten.push(key);
    }
  }
  for (const key in this_parent.chunksRemainsToWrite) {
    if (this_parent.chunksRemainsToWrite.hasOwnProperty(key)) {
      if (beingWritten.indexOf(key) < 0) {
        pendingChunks++;
        pendingChunkNo.push(key);
        sumOfSize += this_parent.chunksRemainsToWrite[key].size;
      }
    }
  }

  if (beingWritten.length > 0) {
    C.log(
      `Download: Total ${
        beingWritten.length
      } chunks are being written, no: ${beingWritten.sort().join(",")}`
    );
  }

  if (pendingChunks) {
    C.log(
      `Download: Total ${pendingChunks} not written chunks, size: ${bytesToMegabytes(
        sumOfSize
      )} MB, Last Written Index ${
        this_parent.lastIndexOfChunkWrittenToDisk
      }, no: ${pendingChunkNo.sort().join(",")}`
    );
  }
};

// Calculate speed and time remaining to download the file
export const updateTimeStatusOfDownloadingFileCommon = (parent) => {
  C.store("Download: update time status of downloading file called");
  const this_parent = parent;
  const { id: fileId, size } = this_parent.streamingFile;

  clearInterval(this_parent.elapsedIntervalId);

  this_parent.elapsedIntervalId = setInterval(() => {
    if (this_parent.fileClosed) {
      clearInterval(this_parent.elapsedIntervalId);
      return;
    }
    // Download speed calc - bytes per second
    const speed = (this_parent.speed =
      this_parent.bytesDownloaded - this_parent.lastBytesDownloaded);

    // Overall speed
    const overAllSpeed = this_parent.bytesDownloaded / this_parent.elapsedTime;

    const speedToCheckTimeRemaining = speed || overAllSpeed;

    // time remaining calc - remaining size to download divides by current download speed
    const timeRemaining = parseInt(
      (parseInt(size) - this_parent.bytesDownloaded) / speedToCheckTimeRemaining
    );

    // elapsed time calc - adding second on every interval
    const elapsedTime = (this_parent.elapsedTime += 1);

    this_parent.lastBytesDownloaded = this_parent.bytesDownloaded;

    // C.log(
    //   "updateTimeStatusOfDownloadingFileCommon",
    //   this_parent.streamingFile
    // );

    C.store(
      `Download: Update file information on UI ID: ${fileId} Speed: ${speed} ETime: ${elapsedTime} TRemain: ${timeRemaining} BytesWritten: ${this_parent.bytesWritten}`
    );
    store.dispatch("updateTimeStatusDownload", {
      fileId,
      bytesWritten: this_parent.bytesWritten,
      speed,
      elapsedTime,
      timeRemaining,
    });
  }, 1000);

  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  this_parent.intervalIdAverageTenSecondSpeed = setInterval(() => {
    if (this_parent.fileClosed) {
      clearInterval(this_parent.intervalIdAverageTenSecondSpeed);
      return;
    }
    // Download speed calc - bytes per 10 seconds
    const speed =
      (this_parent.average10SecSpeed =
        this_parent.bytesDownloaded - this_parent.lastBytesDownloadedInTenSec) /
      10;

    this_parent.lastBytesDownloadedInTenSec = this_parent.bytesDownloaded;

    C.store(
      `Download: Update avg speed info to UI ID: ${fileId} Speed: ${speed}`
    );

    store.dispatch("updateTenSecondAverageDownload", {
      fileId,
      speed,
    });
  }, 10000);
};

// Handle retry of the failed chunks
export const handleDownloadRetry = async (
  parent,
  retries,
  cb = () => {},
  onRetryOver = () => {}
) => {
  const this_parent = parent;
  const file = this_parent.streamingFile;
  const { id: fileId, name: fileName } = file;

  // Return if already retrying chunks
  if (this_parent.retrying) return;
  this_parent.retrying = true;

  clearInterval(this_parent.elapsedIntervalId);
  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  let retrySeconds = (retries + 1) * 10;
  let retryIntervalId;

  const setRetryTimer = (close) => {
    const intervalId = setInterval(() => {
      retrySeconds--;
      _$("#retrySeconds").text(retrySeconds);

      if (retrySeconds === 0) {
        this_parent.retrying = false;
        clearInterval(retryIntervalId);
        cb();
        close();
      }
    }, 1000);

    return intervalId;
  };

  const onBeforeShow = (root, close) => {
    retryIntervalId = setRetryTimer(close);
  };

  const onAfterShow = (root, close) => {
    _$("#skipFile").on("click", () => {
      clearInterval(retryIntervalId);

      const skipFile = window.confirm(
        `Are you sure you want to cancel (${fileName}) file from downloading?`
      );

      if (skipFile) {
        close();
        this_parent.cancel();
        return;
      }

      retryIntervalId = setRetryTimer(close);
    });
  };

  const prepend = `<p class="text-primaryMain">Retrying downloading file <b>(${fileName})</b> In <span id="retrySeconds">${retrySeconds}</span> second(s)</p>`;

  try {
    const resp = await Confirm({
      title: "Retry",
      prepend,
      cancel: null,
      confirm: "Retry Now",
      closable: false,
      prependFooter: utils.generateHTML([
        {
          type: "button#skipFile.primary-button.is-bordered.mr-3",
          text: "Skip File",
        },
      ]),
      onBeforeShow,
      onAfterShow,
    });

    clearInterval(retryIntervalId);
    this_parent.retrying = false;

    if (resp) {
      if (retries < this_parent.maxRetries) {
        cb();
        return;
      }

      onRetryOver();

      store.dispatch("updateFileStatusDownload", {
        fileId,
        updates: {
          status: "failed",
          downloadingError:
            "Downloading failed : Failed while retrying chunk download",
        },
      });

      this_parent.nextFileInQueue();
      return;
    }
  } catch (error) {}
};

// Resume file downloading after pause
export const resumeDownload = (parent, onResume = () => {}) => {
  const this_parent = parent;
  const { isUploading } = store.chunkProcessingStatus;

  if (isUploading) {
    growl(
      t("cantResumeDownloadingTillUploadingFinished"),
      t("uploadingInProgress"),
      5000,
      true
    );
    return;
  }

  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  this_parent.isPause = false;

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "downloading",
    },
  });

  onResume();
};

// Triggers after successful pause of the downloading file
export const pausedDownload = (parent) => {
  const this_parent = parent;
  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  clearInterval(this_parent.elapsedIntervalId);
  clearInterval(this_parent.intervalIdAverageTenSecondSpeed);

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "paused",
      timeStatus: {
        elapsedTime: this_parent.elapsedTime,
        speed: 0,
        timeRemaining: null,
        avg10SecSpeed: 0,
      },
    },
  });

  downloadingQueue.handleUpdateDownloadingStatus(false);
};

// pauses downloading file
export const pauseDownload = (parent, onPause = () => {}) => {
  const this_parent = parent;

  const file = this_parent.streamingFile;
  const { id: fileId } = file;

  onPause();

  this_parent.isPause = true;

  store.dispatch("updateFileStatusDownload", {
    fileId,
    updates: {
      status: "pausing",
    },
  });
};

export const getFileMime = (fileName) => {
  // Extract the file extension
  const extension = fileName.split(".").pop().toLowerCase();

  // Define the accept object
  const accept = {};

  // Map file extensions to MIME types
  const extensions = {
    // Image formats
    png: "image/png",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    gif: "image/gif",
    bmp: "image/bmp",
    webp: "image/webp",
    tiff: "image/tiff",
    svg: "image/svg+xml",
    ico: "image/x-icon",
    apng: "image/apng",
    heif: "image/heif",
    heic: "image/heic",

    // Video formats
    mp4: "video/mp4",
    webm: "video/webm",
    ogg: "video/ogg",
    avi: "video/x-msvideo",
    mov: "video/quicktime",

    // Audio formats
    mp3: "audio/mpeg",
    wav: "audio/wav",
    ogg: "audio/ogg",
    flac: "audio/flac",
    aac: "audio/aac",

    // Document formats
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ppt: "application/vnd.ms-powerpoint",
    pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",

    // Text formats
    txt: "text/plain",
    csv: "text/csv",
    html: "text/html",
    xml: "text/xml",
    json: "application/json",

    // Archive formats
    zip: "application/zip",
    rar: "application/vnd.rar",
    "7z": "application/x-7z-compressed",
    tar: "application/x-tar",
    gz: "application/gzip",

    // Other formats
    exe: "application/x-msdownload",
    dll: "application/octet-stream",
    deb: "application/x-deb",
    rpm: "application/x-rpm",
    dmg: "application/octet-stream",
    // Add more extensions and corresponding MIME types as needed
  };

  // Check if the file extension is supported
  if (extensions.hasOwnProperty(extension)) {
    accept[extensions[extension]] = ["." + extension];
  } else {
    // If the extension is not supported, default to 'application/octet-stream'
    accept["*/*"] = [];
  }
  console.log(accept);
  // Return the object
  return { accept };
};
